/**
 * useShippingMethods Hook
 *
 * Custom hook for fetching shipping methods with server-side orchestration.
 * Calls the /server/shipping/get-methods endpoint which handles:
 * - IsShippingApiService site preference check
 * - External Shipping Service API call (when enabled)
 * - Automatic SFCC fallback on API failure or when disabled
 * - Lead time calculation using SFCC custom attributes
 *
 * @see TSD - Shipping Service Integration (SS-006, SS-007, SS-012)
 */

import {useIsMutating, useQuery, useQueryClient} from '@tanstack/react-query';
import {useShippingMethodsForShipment} from '@salesforce/commerce-sdk-react';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {filterByShippingPriority} from '../utils/shipping-method-priority';
import {cartItemsCacheKey} from '../pages/checkout/util/payment-amount-utils';
import {
  getWhsCodeFromShippingMethod,
  isShippingServiceApiSource,
} from '../utils/shipping-method-whs-code';

/** Server orchestration query: reuse responses across navigations on checkout/cart. */
const STALE_TIME_CHECKOUT_MS = 5 * 60 * 1000;

/** PDP / live customization: do not retain TanStack Query cache; cart line composition changes often. */
const STALE_TIME_PDP_MS = 0;

/**
 * Extract cart items from basket for shipping calculation
 *
 * @param {Object} basket - Current basket data
 * @returns {Array} Cart items formatted for shipping service
 */
function extractCartItems(basket) {
  if (!basket?.productItems) {
    return [];
  }

  return basket.productItems.map((item) => ({
    productId: item.productId,
    sku: item.productId,
    quantity: item.quantity || 1,
    price: item.price,
    leadTime: item.c_leadTime || 0,
    productType: item.c_productType || 'furniture',
    weight: item.c_weight || null,
  }));
}

/**
 * Format shipping address for API request
 *
 * @param {Object} shippingAddress - Shipping address from basket
 * @returns {Object|null} Formatted address for shipping service
 */
function formatAddress(shippingAddress) {
  if (!shippingAddress) {
    return null;
  }

  return {
    postcode: shippingAddress.postalCode || shippingAddress.postcode,
    city: shippingAddress.city,
    stateCode: shippingAddress.stateCode,
    countryCode: shippingAddress.countryCode || 'AU',
  };
}

/**
 * Whether an address has a postcode for the shipping orchestrator.
 *
 * @param {Object|null|undefined} address
 * @returns {boolean}
 */
function hasOrchestratorPostcode(address) {
  if (!address) {
    return false;
  }

  const pc = address.postalCode ?? address.postcode;

  return Boolean(pc != null && String(pc).trim());
}

/**
 * Normalized postcode for query keys (basket or estimate-only address).
 *
 * @param {Object|null|undefined} address
 * @returns {string|undefined}
 */
function getOrchestratorPostcode(address) {
  if (!address) {
    return undefined;
  }

  const pc = address.postalCode ?? address.postcode;

  return pc != null ? String(pc).trim() : undefined;
}

/**
 * Merge SFCC base methods with server-provided enhanced data.
 *
 * Two modes driven by `source`:
 * - **API mode** (`SHIPPING_API` / `SHIPPING_API_HYBRID` / `SHIPPING_OPTIONS_API_MOCK`):
 *   the server has already collapsed external API runs onto SFCC method IDs (see
 *   `collapseApiMethodsOntoSfcc` in `shipping-service.js`). We trust the server list,
 *   keyed by SFCC `id`. SFCC methods absent from the server response are dropped
 *   (per agreed D1 behaviour: API is canonical when enabled). For each kept row we
 *   layer the rich SCAPI metadata (description, promotions, etc.) and compute
 *   `actualPrice` / `sfccPrice` so the UI can render the strikethrough.
 *
 * - **SFCC mode** (`SFCC` / `SFCC_FALLBACK` / null): show every SFCC method with
 *   `priceSource: 'sfcc'`, no API enrichment.
 *
 * @param {Array} sfccMethods - Methods from SFCC commerce API
 * @param {Array} serverMethods - Methods from /get-methods endpoint (SFCC-id-keyed when source is API)
 * @param {string} source - Data source from server response
 * @returns {Array} Merged shipping methods
 */
export function mergeShippingMethods(sfccMethods, serverMethods, source) {
  const isApiSource = isShippingServiceApiSource(source);

  if (isApiSource) {
    if (!serverMethods?.length) {
      return [];
    }

    const sfccMethodMap = new Map((sfccMethods || []).map((m) => [m.id, m]));

    return serverMethods
      .map((serverMethod) => {
        const sfccMethod = sfccMethodMap.get(serverMethod.id);
        const serverPrice = serverMethod.price?.amount ?? serverMethod.price;
        const sfccPrice = sfccMethod?.price ?? sfccMethod?.c_shippingCost ?? null;

        return {
          ...(sfccMethod || {}),
          ...serverMethod,
          id: serverMethod.id,
          actualPrice: serverPrice ?? sfccPrice ?? 0,
          priceSource: 'api',
          sfccPrice,
          leadTime: serverMethod.leadTime || null,
          shippingOptionsApi: serverMethod.shippingOptionsApi || null,
          available: serverMethod.available !== false,
        };
      });
  }

  if (!sfccMethods?.length) {
    return [];
  }

  if (!serverMethods?.length) {
    return sfccMethods.map((method) => ({
      ...method,
      leadTime: null,
      actualPrice: method.price ?? method.c_shippingCost ?? 0,
      priceSource: 'sfcc',
      WhsCode: null,
      available: true,
    }));
  }

  const serverMethodMap = new Map(serverMethods.map((m) => [m.id, m]));

  return sfccMethods.map((sfccMethod) => {
    const serverMethod = serverMethodMap.get(sfccMethod.id);

    if (serverMethod) {
      const isApiSource = source === 'SHIPPING_API' || source === 'SHIPPING_API_HYBRID';
      const serverPrice = serverMethod.price?.amount ?? serverMethod.price;
      const sfccPrice = sfccMethod.price ?? sfccMethod.c_shippingCost;

      const actualPrice = isApiSource ?
        (serverPrice ?? sfccPrice ?? 0) :
        (sfccPrice ?? serverPrice ?? 0);

      return {
        ...sfccMethod,
        actualPrice,
        priceSource: isApiSource ? 'api' : 'sfcc',
        leadTime: serverMethod.leadTime || null,
        sfccPrice: sfccMethod.price,
        available: true,
        WhsCode: getWhsCodeFromShippingMethod(serverMethod) ?? null,
      };
    }

    return {
      ...sfccMethod,
      actualPrice: sfccMethod.price ?? sfccMethod.c_shippingCost ?? 0,
      priceSource: 'sfcc',
      leadTime: null,
      available: true,
      WhsCode: null,
    };
  });
}

/**
 * Custom hook for fetching shipping methods
 *
 * Uses server-side orchestration via /server/shipping/get-methods
 * which handles site preferences, API calls, and fallback logic.
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to enable the query
 * @param {'checkout'|'pdp'} [options.cacheContext='checkout'] - `checkout`: 5min TanStack staleTime for
 *   server + SFCC queries. `pdp`: no client cache (stale immediately) when shipping is shown alongside
 *   frequent product configuration changes.
 * @param {Object|null} [options.estimateAddress] - Delivery address used only for the shipping
 *   orchestrator when the basket has no shipping address (e.g. cart flyout geo estimate).
 *   Never written to the basket.
 * @returns {Object} Shipping methods query result
 */
export function useShippingMethods(options = {}) {
  const {enabled = true, cacheContext = 'checkout', estimateAddress = null} = options;
  const isPdpContext = cacheContext === 'pdp';
  const serverStaleTime = isPdpContext ? STALE_TIME_PDP_MS : STALE_TIME_CHECKOUT_MS;
  const queryClient = useQueryClient();

  const {site} = useMultiSite();
  const siteId = site?.id || 'kl-au';

  const {data: basket} = useCurrentBasket();
  const basketShippingAddress = basket?.shipments?.[0]?.shippingAddress;
  const orchestratorAddress = hasOrchestratorPostcode(basketShippingAddress) ?
    basketShippingAddress :
    estimateAddress;
  const orchestratorPostcode = getOrchestratorPostcode(orchestratorAddress);
  const cartItems = extractCartItems(basket);
  const cartSignature = cartItemsCacheKey(cartItems);

  // Always fetch SFCC methods as the canonical base
  const sfccQuery = useShippingMethodsForShipment(
    {
      parameters: {
        basketId: basket?.basketId,
        shipmentId: 'me',
      },
    },
    {
      enabled: Boolean(basket?.basketId) && enabled,
      staleTime: serverStaleTime,
    }
  );

  const sfccApplicableMethods = sfccQuery.data?.applicableShippingMethods;
  const hasSfccMethods = sfccApplicableMethods && sfccApplicableMethods.length > 0;

  // The server query depends on the FINAL sfccMethodIds. We must NOT fire it until SCAPI
  // has produced a real success response, otherwise we'd send `sfccMethods: []` and the
  // backend (`collapseApiMethodsOntoSfcc`) would warn "no SFCC methods provided" and the
  // returned method IDs would not match anything SCAPI accepts on PUT shipping-method.
  //
  // Why `status === 'success'` rather than `!isFetching`:
  // TanStack Query has a brief `fetchStatus === 'idle'` window for a query that's
  // enabled-but-not-yet-started (e.g. on first mount after the SCAPI cache was
  // invalidated by the address-update mutation). During that window `isFetching === false`
  // and `data === undefined`, so `!isFetching && hasSfccMethods` evaluates to `false`,
  // which is correct — but if SCAPI returns `applicableShippingMethods: []` for any
  // reason (or stale data from a different basket), `hasSfccMethods` could flicker.
  // `status === 'success'` only flips to `true` after a *completed* fetch with our
  // basketId, eliminating the race entirely.
  const sfccQueryReady = sfccQuery.status === 'success' && !sfccQuery.isFetching;

  const sfccMethodsForServer = sfccApplicableMethods?.map(
    (m) => ({
      id: m.id,
      name: m.name,
      description: m.description,
      price: m.price ?? m.c_shippingCost,
      estimatedArrivalTime: m.estimatedArrivalTime || m.c_estimatedArrivalTime,
      c_shippingApiCode: m.c_shippingApiCode || null,
      // FSD DELIVERY-03 #1: pickup methods render "Earliest collection …".
      c_storePickupEnabled: m.c_storePickupEnabled === true,
      // FSD DELIVERY-03 #3: merchant-configured fallback timing copy when
      // timing cannot be calculated.
      c_leadTimeFallbackMessage: m.c_leadTimeFallbackMessage || null,
    })
  );

  // Sort the IDs so the queryKey is order-invariant — SCAPI's
  // `useShippingMethodsForShipment` doesn't guarantee a stable order across cache-hit
  // vs background-refetch (the address-update mutation that runs at the end of the
  // SHIPPING_ADDRESS step invalidates the cache, and the refetched response can return
  // the same methods in a different order). Without sorting, the joined string flips
  // between e.g. "1,11,2,3,…" and "1,2,3,11,…" → TanStack Query treats it as a different
  // query → `/server/shipping/get-methods` is fired twice on entry to SHIPPING_OPTIONS.
  const sfccMethodIds = sfccApplicableMethods
    ?.map((m) => m.id)
    .slice()
    .sort()
    .join(',') || '';

  // KINDW-176 follow-up — pause the orchestrator while any basket mutation
  // is in flight. Removing a complex line item cascades N+1 sequential
  // `removeItemFromBasket` calls (children first, then parent); without
  // this gate the cart signature changes once per delete and the
  // orchestrator fires once per intermediate state. With it we let the
  // cascade run to completion and fetch exactly once for the final cart.
  //
  // `useIsMutating()` is global (not filtered by mutation key — the SDK
  // doesn't tag its mutations), so this also defers during unrelated
  // mutations (e.g. payment, billing address). That trade-off is
  // acceptable: stale shipping-method data lingers for ~1 mutation cycle
  // and self-heals as soon as the queue drains.
  const mutationsInFlight = useIsMutating();

  // Fetch enhanced data from server-side orchestrator.
  // IMPORTANT: This query must wait for the SFCC query to complete first,
  // otherwise the server receives an empty sfccMethods array and falls
  // back to mock data with mismatched IDs.
  const serverEnabled =
    Boolean(basket?.basketId) &&
    enabled &&
    Boolean(orchestratorPostcode) &&
    hasSfccMethods &&
    sfccQueryReady &&
    mutationsInFlight === 0;

  const serverQuery = useQuery({
    queryKey: [
      'shippingMethods',
      'getMethodsV2',
      cacheContext,
      siteId,
      orchestratorPostcode,
      cartSignature,
      sfccMethodIds
    ],
    queryFn: async () => {
      const address = formatAddress(orchestratorAddress);

      if (!address?.postcode) {
        return null;
      }

      // Belt-and-braces guard: the `enabled` flag should already prevent this branch,
      // but if anything ever calls `serverQuery.refetch()` before SCAPI resolves we
      // would silently POST `sfccMethods: []` and pollute the cache with mismatched
      // method IDs. Bail out instead so the caller gets `null` until SCAPI is ready.
      if (!sfccMethodsForServer?.length) {
        return null;
      }

      const response = await fetch('/server/shipping/get-methods', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          siteId,
          address,
          cartItems,
          sfccMethods: sfccMethodsForServer,
        }),
      });

      if (!response.ok) {
        throw new Error(`Shipping endpoint error: ${response.status}`);
      }

      return response.json();
    },
    enabled: serverEnabled,
    staleTime: serverStaleTime,
    retry: 1,
  });

  const source = serverQuery.data?.source || null;
  const apiUsed = isShippingServiceApiSource(source);
  const isFallback = source === 'SFCC_FALLBACK';

  // Anti-flash gate — keep the merged list empty until both queries are
  // FULLY settled (not just successful, but also not currently re-fetching).
  //
  // The crucial case the previous `status === 'success'` check missed:
  // when the shopper switches saved addresses, the address mutation
  // invalidates the SCAPI shipping-methods cache. While SCAPI re-fetches:
  //   - `sfccQuery.status` stays 'success' (cached data is still present)
  //   - `sfccQuery.isFetching` flips to `true`
  //   - `sfccQuery.data` still holds the PREVIOUS address's methods
  //   - `sfccQueryReady` evaluates `false`, so `serverEnabled` is `false`
  //   - `!serverEnabled` would mark the orchestrator as "settled"
  //   → mergeShippingMethods runs against stale SCAPI data with no
  //     orchestrator data and returns SFCC-only methods from the previous
  //     address. That is the flash the shopper sees on every address swap.
  //
  // Treat any in-flight fetch (initial OR background refetch) as
  // "not ready", and require the orchestrator to have actually produced a
  // settled result (success or error) whenever it's expected to run.
  const shouldLoad = enabled && Boolean(basket?.basketId);
  // When the consumer isn't asking us to load (disabled, no basket), the
  // hook returns immediately — no skeleton, no methods.
  const sfccReady = !shouldLoad ||
    (!sfccQuery.isFetching && sfccQuery.status === 'success');
  const serverExpected = shouldLoad && Boolean(orchestratorPostcode);
  // When the orchestrator isn't expected to run at all (e.g. no address
  // yet on a PDP-style consumer), we don't need to wait for it — the
  // SFCC-only branch of mergeShippingMethods can render directly.
  const serverReady = !serverExpected ||
    (!serverQuery.isFetching &&
      (serverQuery.status === 'success' || serverQuery.status === 'error'));

  const isReady = sfccReady && serverReady;

  const mergedMethods = isReady ?
    mergeShippingMethods(
      sfccQuery.data?.applicableShippingMethods,
      serverQuery.data?.methods,
      source
    ) :
    [];

  const filteredMethods = filterByShippingPriority(mergedMethods);

  const sortedMethods = [...filteredMethods].sort((a, b) => {
    const priceA = a.actualPrice ?? a.price ?? 0;
    const priceB = b.actualPrice ?? b.price ?? 0;

    return priceA - priceB;
  });

  /**
   * Fetch applicable methods for a basket snapshot (bypasses TanStack stale cache).
   * Used when another browser tab may have updated the shared basket address.
   *
   * @param {Object} basketSnapshot
   * @returns {Promise<{applicableShippingMethods: Array, source: string|null, apiUsed: boolean, fallback: boolean}>}
   */
  const fetchApplicableMethodsForBasket = async (basketSnapshot) => {
    if (!basketSnapshot?.basketId) {
      return {
        applicableShippingMethods: [],
        source: null,
        apiUsed: false,
        fallback: false,
      };
    }

    const addressForFetch = basketSnapshot?.shipments?.[0]?.shippingAddress;
    const cartItemsForFetch = extractCartItems(basketSnapshot);

    const sfccResult = await sfccQuery.refetch();
    const sfccApplicable = sfccResult.data?.applicableShippingMethods ?? [];
    const sfccMethodsForServer = sfccApplicable.map((m) => ({
      id: m.id,
      name: m.name,
      description: m.description,
      price: m.price ?? m.c_shippingCost,
      estimatedArrivalTime: m.estimatedArrivalTime || m.c_estimatedArrivalTime,
      c_shippingApiCode: m.c_shippingApiCode || null,
      c_storePickupEnabled: m.c_storePickupEnabled === true,
      c_leadTimeFallbackMessage: m.c_leadTimeFallbackMessage || null,
    }));

    const formattedAddress = formatAddress(addressForFetch);
    let serverData = null;

    if (formattedAddress?.postcode && sfccMethodsForServer.length > 0) {
      const response = await fetch('/server/shipping/get-methods', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          siteId,
          address: formattedAddress,
          cartItems: cartItemsForFetch,
          sfccMethods: sfccMethodsForServer,
        }),
      });

      if (response.ok) {
        serverData = await response.json();
      }
    }

    const fetchSource = serverData?.source || null;
    const merged = mergeShippingMethods(sfccApplicable, serverData?.methods, fetchSource);
    const filtered = filterByShippingPriority(merged);
    const sorted = [...filtered].sort((a, b) => {
      const priceA = a.actualPrice ?? a.price ?? 0;
      const priceB = b.actualPrice ?? b.price ?? 0;

      return priceA - priceB;
    });

    await queryClient.invalidateQueries({
      queryKey: ['shippingMethods', 'getMethodsV2', cacheContext, siteId],
    });
    await serverQuery.refetch();

    return {
      applicableShippingMethods: sorted,
      source: fetchSource,
      apiUsed: isShippingServiceApiSource(fetchSource),
      fallback: fetchSource === 'SFCC_FALLBACK',
    };
  };

  return {
    data: {
      applicableShippingMethods: sortedMethods,
      defaultShippingMethodId: null,
      source,
      apiUsed,
      fallback: isFallback || serverQuery.isError,
    },

    // Use `isFetching` (not `isLoading`) for the SCAPI query so the
    // skeleton stays up during background refetches — most notably the
    // refetch triggered by an address mutation. `isLoading` is only true
    // on the very first fetch (no cached data), which would hide the
    // skeleton while SCAPI is re-fetching with stale data still in cache.
    //
    // The composite "not ready" expression also covers the brief window
    // where SCAPI is settled but the orchestrator hasn't started yet
    // (`enabled` flipped to true within this render — TanStack updates
    // the observer state on the next tick).
    isLoading: !isReady,
    isError: sfccQuery.isError,
    error: sfccQuery.error || serverQuery.error,

    sfccQuery,
    serverQuery,

    refetch: async (options = {}) => {
      if (options.basket) {
        return fetchApplicableMethodsForBasket(options.basket);
      }

      await Promise.all([sfccQuery.refetch(), serverQuery.refetch()]);

      return {
        applicableShippingMethods: sortedMethods,
        source,
        apiUsed,
        fallback: isFallback || serverQuery.isError,
      };
    },

    fetchApplicableMethodsForBasket,
  };
}

/**
 * Get lead time for a specific shipping method
 *
 * @param {string} methodId - Shipping method ID
 * @param {Object} options - Hook options
 * @param {'checkout'|'pdp'} [options.cacheContext='checkout'] - Same semantics as {@link useShippingMethods}.
 * @returns {Object} Lead time query result
 */
export function useShippingMethodLeadTime(methodId, options = {}) {
  const {enabled = true, cacheContext = 'checkout'} = options;
  const leadStaleTime = cacheContext === 'pdp' ? STALE_TIME_PDP_MS : STALE_TIME_CHECKOUT_MS;

  const {data: basket} = useCurrentBasket();
  const shippingAddress = basket?.shipments?.[0]?.shippingAddress;

  return useQuery({
    queryKey: ['shippingMethod', 'leadTime', cacheContext, methodId, shippingAddress?.postalCode],
    queryFn: async () => {
      const postcode = shippingAddress?.postalCode;

      if (!postcode) {
        return null;
      }

      const response = await fetch(
        `/server/shipping/cost/${methodId}?postcode=${postcode}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch lead time');
      }

      return response.json();
    },
    enabled: Boolean(methodId) && Boolean(shippingAddress) && enabled,
    staleTime: leadStaleTime,
  });
}

export default useShippingMethods;
